/**
 * LabsBucket — Smart Lab Ordering (Finalization)
 *
 * Design:
 *  1. Voiced/Explicit orders — "Voiced" green badge, auto-confirm with one click
 *  2. Suggested for Diagnosis — group checkboxes, auto-attach DX, "Add Selected" at group level
 *  3. Order Sets — inline rename, clean display
 */
import React from 'react';
import { api } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  TestTubes, Plus, X, Check, Loader2, AlertCircle, Mic,
  ChevronDown, Edit3, Square, CheckSquare
} from 'lucide-react';
import { toast } from 'sonner';
import { BucketHeader, EmptyBucket } from './shared';

export function LabsBucket({ enc, encounterId, fetchEncounter, setEncounter, outputs }) {
  const [labOrders, setLabOrders] = React.useState(enc?.lab_orders || []);
  const [showCatalog, setShowCatalog] = React.useState(false);
  const [catalogSearch, setCatalogSearch] = React.useState('');
  const [catalogResults, setCatalogResults] = React.useState([]);
  const [catalogLoading, setCatalogLoading] = React.useState(false);

  // Search the lab catalog when the user types
  React.useEffect(() => {
    if (!catalogSearch.trim() || catalogSearch.length < 2) {
      setCatalogResults([]);
      return;
    }
    setCatalogLoading(true);
    const timer = setTimeout(() => {
      api.get(`/lab-catalog?search=${encodeURIComponent(catalogSearch)}&limit=15`)
        .then(r => setCatalogResults(r.data.results || r.data.labs || []))
        .catch(() => setCatalogResults([]))
        .finally(() => setCatalogLoading(false));
    }, 250);
    return () => clearTimeout(timer);
  }, [catalogSearch]);
  const [ordering, setOrdering] = React.useState(null);
  const [suggestions, setSuggestions] = React.useState([]);
  const [customSets, setCustomSets] = React.useState([]);
  const [showSaveSet, setShowSaveSet] = React.useState(false);
  const [setName, setSetName] = React.useState('');
  const [setScope, setSetScope] = React.useState('clinic');
  const [renamingSet, setRenamingSet] = React.useState(null); // set id being renamed
  const [renameValue, setRenameValue] = React.useState('');
  // Group selections: { [logic_pack_id]: Set of suggestion_ids }
  const [groupSelections, setGroupSelections] = React.useState({});
  // Voiced + AI group selections (parallel to Diagnosis group pattern)
  const [voicedSelections, setVoicedSelections] = React.useState(null);   // null = not yet initialized
  const [aiSelections, setAiSelections] = React.useState(null);

  const labActions = (enc?.suggested_actions || []).filter(a =>
    a.action_type === 'lab_order' || a.task_type === 'LAB' ||
    (a.action_name || '').toLowerCase().includes('lab') ||
    (a.action_name || '').toLowerCase().includes('a1c') ||
    (a.action_name || '').toLowerCase().includes('cbc') ||
    (a.action_name || '').toLowerCase().includes('panel')
  );

  const problems = enc?.note?.problem_oriented_ap || [];
  const dxCodes = outputs?.billing_suggestion?.diagnosis_codes || [];
  const availableDx = problems.map((p, i) => ({
    code: p.icd_code || `P${i+1}`,
    label: p.problem_name || p.icd_code || `Problem ${i+1}`
  }));
  dxCodes.forEach(c => { if (!availableDx.find(d => d.code === c)) availableDx.push({ code: c, label: c }); });

  // Load suggestions
  React.useEffect(() => {
    api.get(`/encounters/${encounterId}/lab-suggestions`).then(r => {
      const s = r.data.suggestions || [];
      setSuggestions(s);
      // Pre-select all labs in each group
      const selections = {};
      s.forEach(g => {
        selections[g.logic_pack_id] = new Set(g.labs.map(l => l.suggestion_id));
      });
      setGroupSelections(selections);
    }).catch(() => {});
  }, [encounterId, labOrders.length]);

  // Lab family relationships: if a "parent" lab is ordered, its "children" are redundant
  // Keys are lab codes that subsume others; values are arrays of redundant codes
  const LAB_FAMILY_MAP = React.useMemo(() => ({
    '80053': ['80048', '80069', '80050'],       // CMP supersedes BMP, Renal Panel, General Health Panel
    '85025': ['85027', '85004', '85014'],        // CBC w/ diff supersedes CBC w/o diff and components
    '80061': ['82465', '83718', '84478'],        // Lipid panel supersedes individual lipid components
    '80091': ['84443', '84481', '84480', '84439', '84436', '86200', '86376'],  // Thyroid Panel supersedes TSH, T3 free/total, T4 free/total, TPO Ab
    '80048': [],                                 // BMP (no subsumed codes)
    '80050': ['80053', '80048'],                 // General Health Panel → CMP and BMP covered
  }), []);

  // Build global set of codes made redundant by already-ordered labs
  const redundantCodes = React.useMemo(() => {
    const redundant = new Set();
    labOrders.forEach(o => {
      const family = LAB_FAMILY_MAP[o.lab_code] || [];
      family.forEach(c => redundant.add(c));
    });
    return redundant;
  }, [labOrders, LAB_FAMILY_MAP]);

  // Build global set of already-ordered/selected lab codes (for cross-group dedup)
  const alreadyOrderedCodes = React.useMemo(() => {
    const codes = new Set(labOrders.map(o => o.lab_code));
    return codes;
  }, [labOrders]);

  React.useEffect(() => {
    api.get('/order-sets').then(r => setCustomSets(r.data.sets || [])).catch(() => {});
  }, []);

  const handleOrderLab = async (labCode, dxCode) => {
    setOrdering(labCode);
    try {
      const res = await api.post(`/encounters/${encounterId}/lab-orders`, {
        lab_code: labCode, diagnosis_code: dxCode, urgency: 'routine', notes: ''
      });
      if (res.data.success) { toast.success(`Ordered: ${res.data.order?.lab_name || labCode}`); fetchEncounter(); }
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setOrdering(null); }
  };

  const handleAddGroupSelected = async (group) => {
    const selected = groupSelections[group.logic_pack_id] || new Set();
    const selectedLabs = group.labs.filter(l => selected.has(l.suggestion_id));
    if (!selectedLabs.length) { toast.error('No labs selected'); return; }
    const dxCode = group.linked_dx_code || (availableDx[0]?.code || '');
    setOrdering(`group_${group.logic_pack_id}`);
    try {
      for (const lab of selectedLabs) {
        await api.post(`/encounters/${encounterId}/lab-orders`, {
          lab_code: lab.code, diagnosis_code: dxCode, urgency: 'routine', notes: ''
        });
      }
      toast.success(`${selectedLabs.length} lab(s) ordered — linked to ${dxCode || 'diagnosis'}`);
      fetchEncounter();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setOrdering(null); }
  };

  const handleDismissSuggestion = async (suggestionId, packId) => {
    try {
      await api.post(`/encounters/${encounterId}/lab-suggestions/dismiss`, { suggestion_id: suggestionId });
      setSuggestions(prev => prev.map(g => ({
        ...g, labs: g.labs.filter(l => l.suggestion_id !== suggestionId)
      })).filter(g => g.labs.length > 0));
      setGroupSelections(prev => {
        const next = { ...prev };
        if (next[packId]) { next[packId] = new Set([...next[packId]].filter(id => id !== suggestionId)); }
        return next;
      });
    } catch { toast.error('Failed'); }
  };

  const handleAction = async (actionId, action) => {
    try {
      await api.post(`/encounters/${encounterId}/suggested-actions/${actionId}/execute`, { action });
      fetchEncounter();
      toast.success(action === 'done_by_provider' ? 'Added to order list' : action === 'send_to_nurse' ? 'Sent to nurse' : 'Dismissed');
    } catch { toast.error('Failed'); }
  };

  const toggleLabInGroup = (packId, suggestionId) => {
    setGroupSelections(prev => {
      const current = new Set(prev[packId] || []);
      if (current.has(suggestionId)) current.delete(suggestionId);
      else current.add(suggestionId);
      return { ...prev, [packId]: current };
    });
  };

  const toggleAllInGroup = (packId, allIds) => {
    setGroupSelections(prev => {
      const current = prev[packId] || new Set();
      const allSelected = allIds.every(id => current.has(id));
      return { ...prev, [packId]: allSelected ? new Set() : new Set(allIds) };
    });
  };

  const handleRenameSet = async (setId) => {
    if (!renameValue.trim()) return;
    try {
      await api.put(`/order-sets/${setId}`, { name: renameValue.trim() });
      api.get('/order-sets').then(r => setCustomSets(r.data.sets || []));
      setRenamingSet(null);
      setRenameValue('');
      toast.success('Order set renamed');
    } catch { toast.error('Failed'); }
  };

  const totalCount = labActions.length + labOrders.length;

  // Voiced labs = explicitly verbalized in transcript
  const voicedActions = labActions.filter(a => a.verbalized || a.confidence_label === 'voiced' || a.source === 'verbalized_order');
  const otherActions  = labActions.filter(a => !a.verbalized && a.confidence_label !== 'voiced' && a.source !== 'verbalized_order');

  // Initialize selections (all pre-checked) whenever the action lists change
  React.useEffect(() => {
    if (voicedActions.length > 0 && voicedSelections === null) {
      setVoicedSelections(new Set(voicedActions.map(a => a.id)));
    }
  }, [voicedActions.length]); // eslint-disable-line
  React.useEffect(() => {
    if (otherActions.length > 0 && aiSelections === null) {
      setAiSelections(new Set(otherActions.map(a => a.id)));
    }
  }, [otherActions.length]); // eslint-disable-line

  const effectiveVoicedSel = voicedSelections ?? new Set(voicedActions.map(a => a.id));
  const effectiveAiSel     = aiSelections     ?? new Set(otherActions.map(a => a.id));

  return (
    <div className="space-y-5" data-testid="finalize-labs-bucket">
      {/* Header */}
      <div className="flex items-center justify-between">
        <BucketHeader icon={TestTubes} title="Labs" count={totalCount}
          status={totalCount > 0 && labActions.every(a => a.status !== 'pending') ? 'complete' : totalCount > 0 ? 'pending' : undefined} />
        <Button variant="outline" size="sm" className="h-7 text-[10px] gap-1"
          onClick={() => setShowCatalog(!showCatalog)} data-testid="labs-order-single-btn">
          <Plus className="w-3 h-3" /> Individual Lab
        </Button>
      </div>

      {/* Individual Lab Search */}
      {showCatalog && (
        <div className="rounded-lg border p-3 space-y-2 animate-in fade-in-0 slide-in-from-top-1 duration-100">
          <input type="text" placeholder="Search labs (e.g. CBC, HbA1c, testosterone, TSH)..." value={catalogSearch}
            onChange={e => setCatalogSearch(e.target.value)} autoFocus
            className="w-full text-xs border rounded px-2 py-1.5 bg-background" />
          <div className="max-h-48 overflow-y-auto space-y-0.5">
            {catalogLoading && <p className="text-[10px] text-muted-foreground py-2 text-center">Searching...</p>}
            {!catalogLoading && catalogSearch.length >= 2 && catalogResults.length === 0 && (
              <p className="text-[10px] text-muted-foreground py-2 text-center">No labs found for "{catalogSearch}"</p>
            )}
            {!catalogLoading && catalogSearch.length < 2 && (
              <p className="text-[10px] text-muted-foreground py-2 text-center">Type at least 2 characters to search</p>
            )}
            {catalogResults.slice(0, 15).map(lab => (
              <div key={lab.code} className="flex items-center justify-between py-1.5 text-xs border-b last:border-0">
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-[8px] font-mono">{lab.code}</Badge>
                  <span>{lab.name}</span>
                </div>
                {availableDx.length > 0 ? (
                  <select className="text-[10px] border rounded px-1.5 py-0.5 bg-background"
                    defaultValue="" onChange={e => { if (e.target.value) handleOrderLab(lab.code, e.target.value); }}>
                    <option value="">+ Order / Link Dx</option>
                    {availableDx.map(d => <option key={d.code} value={d.code}>{d.code} — {d.label}</option>)}
                  </select>
                ) : (
                  <Button size="sm" className="h-6 text-[9px]" onClick={() => handleOrderLab(lab.code, '')}>+ Add</Button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Ordered Labs (confirmed) */}
      {labOrders.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Ordered Labs</p>
          {labOrders.map((order, i) => (
            <div key={order.id} className="rounded-md border p-2.5 bg-emerald-50/30 flex items-center gap-2" data-testid={`lab-order-${i}`}>
              <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
              <Badge variant="outline" className="text-[8px] font-mono bg-emerald-50 text-emerald-700">{order.lab_code}</Badge>
              <span className="text-xs font-medium flex-1">{order.lab_name}</span>
              {order.linked_diagnosis && (
                <Badge variant="outline" className="text-[8px] font-mono text-amber-700 bg-amber-50 border-amber-200">{order.linked_diagnosis}</Badge>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ── Section 1: Verbalized from Visit — checkbox group (GREEN) ── */}
      {voicedActions.length > 0 && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50/20 p-3" data-testid="voiced-labs-group">
          <div className="flex items-center gap-2 mb-2">
            <button onClick={() => {
              const allIds = voicedActions.filter(a => a.status === 'pending').map(a => a.id);
              const allSel = allIds.every(id => effectiveVoicedSel.has(id));
              setVoicedSelections(allSel ? new Set() : new Set(allIds));
            }} className="shrink-0">
              {voicedActions.filter(a=>a.status==='pending').every(a=>effectiveVoicedSel.has(a.id))
                ? <CheckSquare className="w-4 h-4 text-emerald-600" />
                : <Square className="w-4 h-4 text-emerald-300" />}
            </button>
            <div className="flex items-center gap-1.5 flex-1">
              <Mic className="w-3 h-3 text-emerald-600 shrink-0" />
              <p className="text-[11px] font-semibold text-emerald-700">Verbalized from Visit</p>
              <span className="text-[9px] text-emerald-600/70">— ordered aloud, DX auto-linked</span>
            </div>
            {availableDx[0] && (
              <Badge variant="outline" className="text-[8px] font-mono bg-amber-50 text-amber-700 border-amber-200 shrink-0">{availableDx[0].code}</Badge>
            )}
          </div>
          <div className="space-y-1.5 pl-6">
            {voicedActions.map((action, i) => {
              const isPending = action.status === 'pending';
              const isChecked = effectiveVoicedSel.has(action.id);
              if (!isPending) return (
                <div key={action.id} className="flex items-center gap-2 opacity-50 py-0.5">
                  <Check className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                  <span className="text-xs line-through text-muted-foreground flex-1">{action.action_name}</span>
                  <span className="text-[9px] text-emerald-500">ordered</span>
                </div>
              );
              return (
                <div key={action.id} className="flex items-center gap-2 py-0.5">
                  <button onClick={() => setVoicedSelections(prev => {
                    const next = new Set(prev || []);
                    isChecked ? next.delete(action.id) : next.add(action.id);
                    return next;
                  })} className="shrink-0" data-testid={`voiced-check-${i}`}>
                    {isChecked ? <CheckSquare className="w-3.5 h-3.5 text-emerald-600" /> : <Square className="w-3.5 h-3.5 text-muted-foreground/40" />}
                  </button>
                  {action.lab_code && <Badge variant="outline" className="text-[8px] font-mono bg-emerald-50 text-emerald-700 border-emerald-200 shrink-0">{action.lab_code}</Badge>}
                  <span className="text-xs flex-1 font-medium">{action.action_name}</span>
                  <Badge className="text-[8px] bg-emerald-100 text-emerald-700 border-emerald-200 gap-0.5 shrink-0">
                    <Mic className="w-2 h-2" /> Voiced
                  </Badge>
                </div>
              );
            })}
          </div>
          {voicedActions.some(a => a.status === 'pending') && (
            <div className="flex items-center gap-2 mt-3 pt-2 border-t border-emerald-100">
              <Button size="sm"
                className="h-7 text-[10px] gap-1.5 flex-1 bg-emerald-600 hover:bg-emerald-700"
                disabled={effectiveVoicedSel.size === 0 || ordering === 'voiced_group'}
                onClick={async () => {
                  setOrdering('voiced_group');
                  const autoDx = availableDx[0]?.code || '';
                  const toOrder = voicedActions.filter(a => a.status === 'pending' && effectiveVoicedSel.has(a.id));
                  try {
                    for (const action of toOrder) {
                      if (action.lab_code) await api.post(`/encounters/${encounterId}/lab-orders`, { lab_code: action.lab_code, diagnosis_code: autoDx, urgency: 'routine', notes: '' }).catch(()=>{});
                      await api.post(`/encounters/${encounterId}/suggested-actions/${action.id}/execute`, { action: 'done_by_provider' }).catch(()=>{});
                    }
                    toast.success(`${toOrder.length} voiced lab${toOrder.length!==1?'s':''} ordered${autoDx?' → '+autoDx:''}`);
                    fetchEncounter(); setVoicedSelections(null);
                  } catch { toast.error('Failed'); } finally { setOrdering(null); }
                }} data-testid="add-voiced-labs-btn">
                {ordering === 'voiced_group' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                Add {effectiveVoicedSel.size > 0 ? `${effectiveVoicedSel.size} selected` : 'selected'} lab{effectiveVoicedSel.size!==1?'s':''}
                {availableDx[0] && <span className="opacity-70 ml-0.5">→ {availableDx[0].code}</span>}
              </Button>
              <span className="text-[9px] text-emerald-600/70 shrink-0">DX auto-attached</span>
            </div>
          )}
        </div>
      )}

      {/* ── Section 2: AI Suggestions — checkbox group (BLUE) ── */}
      {otherActions.length > 0 && (
        <div className="rounded-lg border border-sky-200 bg-sky-50/20 p-3" data-testid="ai-labs-group">
          <div className="flex items-center gap-2 mb-2">
            <button onClick={() => {
              const allIds = otherActions.filter(a => a.status === 'pending').map(a => a.id);
              const allSel = allIds.every(id => effectiveAiSel.has(id));
              setAiSelections(allSel ? new Set() : new Set(allIds));
            }} className="shrink-0">
              {otherActions.filter(a=>a.status==='pending').every(a=>effectiveAiSel.has(a.id))
                ? <CheckSquare className="w-4 h-4 text-sky-600" />
                : <Square className="w-4 h-4 text-sky-300" />}
            </button>
            <p className="text-[11px] font-semibold text-sky-700 flex-1">AI Suggestions</p>
            <span className="text-[9px] text-sky-600/70">— detected from transcript context</span>
          </div>
          <div className="space-y-1.5 pl-6">
            {otherActions.map((action, i) => {
              const isPending = action.status === 'pending';
              const isChecked = effectiveAiSel.has(action.id);
              if (!isPending) return (
                <div key={action.id} className="flex items-center gap-2 opacity-50 py-0.5">
                  <Check className="w-3.5 h-3.5 text-sky-400 shrink-0" />
                  <span className="text-xs line-through text-muted-foreground flex-1">{action.action_name}</span>
                </div>
              );
              return (
                <div key={action.id} className="flex items-center gap-2 py-0.5">
                  <button onClick={() => setAiSelections(prev => {
                    const next = new Set(prev || []);
                    isChecked ? next.delete(action.id) : next.add(action.id);
                    return next;
                  })} className="shrink-0" data-testid={`ai-check-${i}`}>
                    {isChecked ? <CheckSquare className="w-3.5 h-3.5 text-sky-600" /> : <Square className="w-3.5 h-3.5 text-muted-foreground/40" />}
                  </button>
                  <span className="text-xs flex-1 font-medium">{action.action_name}</span>
                  <Badge variant="outline" className="text-[8px] bg-slate-50 text-slate-500 border-slate-200 shrink-0">
                    AI: {action.confidence_label || 'uncertain'}
                  </Badge>
                </div>
              );
            })}
          </div>
          {otherActions.some(a => a.status === 'pending') && (
            <div className="flex items-center gap-2 mt-3 pt-2 border-t border-sky-100">
              <Button size="sm"
                className="h-7 text-[10px] gap-1.5 flex-1 bg-sky-600 hover:bg-sky-700"
                disabled={effectiveAiSel.size === 0 || ordering === 'ai_group'}
                onClick={async () => {
                  setOrdering('ai_group');
                  const toConfirm = otherActions.filter(a => a.status === 'pending' && effectiveAiSel.has(a.id));
                  try {
                    for (const action of toConfirm) await api.post(`/encounters/${encounterId}/suggested-actions/${action.id}/execute`, { action: 'done_by_provider' }).catch(()=>{});
                    toast.success(`${toConfirm.length} AI suggestion${toConfirm.length!==1?'s':''} confirmed`);
                    fetchEncounter(); setAiSelections(null);
                  } catch { toast.error('Failed'); } finally { setOrdering(null); }
                }} data-testid="confirm-ai-labs-btn">
                {ordering === 'ai_group' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                Confirm {effectiveAiSel.size > 0 ? `${effectiveAiSel.size} selected` : 'selected'}
              </Button>
            </div>
          )}
        </div>
      )}


      {/* ── Section 3: Suggested for Diagnosis (checkboxes) ── */}
      {suggestions.length > 0 && (
        <div className="space-y-3">
          <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Suggested for this diagnosis</p>
          {suggestions.map(group => {
            const allIds = group.labs.map(l => l.suggestion_id);
            const selected = groupSelections[group.logic_pack_id] || new Set();
            const allSelected = allIds.length > 0 && allIds.every(id => selected.has(id));
            const noneSelected = !allIds.some(id => selected.has(id));
            const selectedCount = allIds.filter(id => selected.has(id)).length;
            const dxForGroup = group.linked_dx_code || (availableDx[0]?.code || '');
            const isGroupOrdering = ordering === `group_${group.logic_pack_id}`;

            return (
              <div key={group.logic_pack_id} className="rounded-lg border border-sky-100 bg-sky-50/20 p-3"
                data-testid={`suggestion-group-${group.logic_pack_id}`}>
                {/* Group header with Select All + DX label */}
                <div className="flex items-center gap-2 mb-2">
                  <button
                    onClick={() => toggleAllInGroup(group.logic_pack_id, allIds)}
                    className="text-sky-600 hover:text-sky-800 shrink-0"
                    data-testid={`group-select-all-${group.logic_pack_id}`}>
                    {allSelected
                      ? <CheckSquare className="w-4 h-4 text-sky-600" />
                      : noneSelected
                        ? <Square className="w-4 h-4 text-sky-300" />
                        : <CheckSquare className="w-4 h-4 text-sky-400 opacity-60" />}
                  </button>
                  <p className="text-[11px] text-sky-700 font-semibold flex-1">{group.rationale}</p>
                  {dxForGroup && (
                    <Badge variant="outline" className="text-[8px] font-mono text-amber-700 bg-amber-50 border-amber-200 shrink-0">
                      {dxForGroup}
                    </Badge>
                  )}
                </div>

                {/* Lab rows with checkboxes */}
                <div className="space-y-1.5 pl-6">
                  {group.labs.map(lab => {
                    const isChecked = selected.has(lab.suggestion_id);
                    // Cross-group dedup: check if this lab code is already ordered OR selected in another group OR made redundant
                    const isAlreadyOrdered = alreadyOrderedCodes.has(lab.code);
                    const isRedundant = redundantCodes.has(lab.code);
                    const isSelectedInOtherGroup = !isAlreadyOrdered && !isRedundant && suggestions.some(g =>
                      g.logic_pack_id !== group.logic_pack_id &&
                      g.labs.some(l => l.code === lab.code && (groupSelections[g.logic_pack_id] || new Set()).has(l.suggestion_id))
                    );
                    const isDuplicate = isAlreadyOrdered || isRedundant || isSelectedInOtherGroup;
                    const dupeReason = isAlreadyOrdered ? 'ordered' : isRedundant ? 'covered by ordered panel' : 'selected';
                    return (
                      <div key={lab.suggestion_id} className={`flex items-center gap-2 ${isDuplicate ? 'opacity-40' : ''}`}>
                        <button
                          onClick={() => !isDuplicate && toggleLabInGroup(group.logic_pack_id, lab.suggestion_id)}
                          disabled={isDuplicate}
                          className={`shrink-0 ${isDuplicate ? 'cursor-not-allowed' : ''}`}
                          data-testid={`lab-checkbox-${lab.code}`}>
                          {isDuplicate
                            ? <Check className="w-3.5 h-3.5 text-emerald-400" title={isAlreadyOrdered ? 'Already ordered' : 'Selected in another group'} />
                            : isChecked
                              ? <CheckSquare className="w-3.5 h-3.5 text-sky-600" />
                              : <Square className="w-3.5 h-3.5 text-muted-foreground/40" />}
                        </button>
                        <Badge variant="outline" className={`text-[8px] font-mono shrink-0 ${isDuplicate ? 'bg-muted text-muted-foreground' : ''}`}>{lab.code}</Badge>
                        <span className={`text-xs flex-1 ${isDuplicate ? 'line-through text-muted-foreground' : ''}`}>{lab.name}</span>
                        {isDuplicate ? (
                          <span className="text-[9px] text-emerald-600 shrink-0">
                            {dupeReason}
                          </span>
                        ) : (
                          <button onClick={() => handleDismissSuggestion(lab.suggestion_id, group.logic_pack_id)}
                            className="text-[9px] text-muted-foreground hover:text-foreground shrink-0 px-1"
                            data-testid={`dismiss-${lab.code}`}>
                            dismiss
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Add Selected button */}
                <div className="flex items-center gap-2 mt-3 pt-2 border-t border-sky-100">
                  <Button size="sm" className="h-7 text-[10px] gap-1.5 flex-1 bg-sky-600 hover:bg-sky-700"
                    onClick={() => handleAddGroupSelected(group)}
                    disabled={noneSelected || isGroupOrdering}
                    data-testid={`add-group-${group.logic_pack_id}`}>
                    {isGroupOrdering ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                    Add {selectedCount > 0 ? `${selectedCount} selected` : 'selected'} lab{selectedCount !== 1 ? 's' : ''}
                    {dxForGroup && <span className="opacity-70 ml-0.5">→ {dxForGroup}</span>}
                  </Button>
                  <span className="text-[9px] text-muted-foreground">DX auto-attached</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Section 4: Order Sets ── */}
      {customSets.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Your Order Sets</p>
          {customSets.map(set => {
            const isRenaming = renamingSet === set.id;
            const displayName = set.name?.startsWith('TEST_') ? set.name.replace(/TEST_/, '').replace(/_[a-f0-9]{7,}$/, '').replace(/_/g, ' ') : set.name;
            const hasUglyName = set.name?.startsWith('TEST_');
            return (
              <div key={set.id} className="flex items-center justify-between py-2 border-b last:border-0"
                data-testid={`order-set-${set.id}`}>
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  {isRenaming ? (
                    <div className="flex items-center gap-1.5 flex-1">
                      <input type="text" value={renameValue} onChange={e => setRenameValue(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') handleRenameSet(set.id); if (e.key === 'Escape') { setRenamingSet(null); setRenameValue(''); }}}
                        className="flex-1 text-xs border rounded px-2 py-1 bg-background" autoFocus
                        data-testid={`rename-input-${set.id}`} />
                      <Button size="sm" className="h-6 text-[9px] px-2" onClick={() => handleRenameSet(set.id)}>Save</Button>
                      <Button size="sm" variant="ghost" className="h-6 text-[9px]" onClick={() => { setRenamingSet(null); setRenameValue(''); }}>✕</Button>
                    </div>
                  ) : (
                    <>
                      <span className={`text-xs font-medium ${hasUglyName ? 'text-amber-700' : ''}`}>{displayName}</span>
                      {hasUglyName && (
                        <span className="text-[8px] text-amber-600 italic">rename?</span>
                      )}
                      <Badge variant="outline" className="text-[8px]">{set.scope}</Badge>
                      <span className="text-[9px] text-muted-foreground">{set.labs?.length || 0} labs</span>
                      <button onClick={() => { setRenamingSet(set.id); setRenameValue(displayName); }}
                        className="text-muted-foreground hover:text-foreground ml-0.5 opacity-50 hover:opacity-100"
                        data-testid={`rename-btn-${set.id}`}>
                        <Edit3 className="w-3 h-3" />
                      </button>
                    </>
                  )}
                </div>
                {!isRenaming && availableDx.length > 0 && (
                  <select className="text-[10px] border rounded px-1.5 py-1 bg-background ml-2"
                    defaultValue=""
                    onChange={async e => {
                      if (!e.target.value) return;
                      setOrdering(`set_${set.id}`);
                      try {
                        for (const lab of (set.labs || [])) {
                          await api.post(`/encounters/${encounterId}/lab-orders`, {
                            lab_code: lab.lab_code || lab.code, diagnosis_code: e.target.value, urgency: 'routine', notes: ''
                          });
                        }
                        toast.success(`${displayName}: ${set.labs?.length} labs ordered`);
                        fetchEncounter();
                      } catch { toast.error('Failed'); }
                      finally { setOrdering(null); e.target.value = ''; }
                    }}>
                    <option value="">Order Set</option>
                    {availableDx.map(d => <option key={d.code} value={d.code}>{d.code} — {d.label}</option>)}
                  </select>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Save as Order Set */}
      {labOrders.length > 0 && (
        <div className="pt-2 border-t">
          {!showSaveSet ? (
            <Button variant="outline" size="sm" className="text-[10px] gap-1" onClick={() => setShowSaveSet(true)}>
              <Plus className="w-3 h-3" /> Save Current Labs as Order Set
            </Button>
          ) : (
            <div className="rounded-lg border p-3 space-y-2 animate-in fade-in-0 slide-in-from-top-1 duration-100">
              <input type="text" placeholder="Order set name (e.g. Diabetic Panel, HTN Workup)..."
                value={setName} onChange={e => setSetName(e.target.value)}
                className="w-full text-xs border rounded px-2 py-1.5 bg-background" autoFocus />
              <div className="flex gap-1.5">
                {['provider', 'clinic'].map(scope => (
                  <button key={scope} onClick={() => setSetScope(scope)}
                    className={`px-2.5 py-1 rounded text-[10px] font-medium transition-colors ${setScope === scope ? 'bg-foreground text-background' : 'bg-muted text-muted-foreground'}`}>
                    {scope === 'provider' ? 'My Set' : 'Clinic-Wide'}
                  </button>
                ))}
              </div>
              <div className="flex gap-1.5">
                <Button size="sm" className="text-[10px]" disabled={!setName.trim()} onClick={async () => {
                  try {
                    await api.post('/order-sets', {
                      name: setName, description: '', scope: setScope,
                      labs: labOrders.map(o => ({ code: o.lab_code, name: o.lab_name })),
                    });
                    toast.success(`"${setName}" saved`);
                    setShowSaveSet(false); setSetName('');
                    api.get('/order-sets').then(r => setCustomSets(r.data.sets || []));
                  } catch { toast.error('Failed'); }
                }}>Save</Button>
                <Button variant="ghost" size="sm" className="text-[10px]" onClick={() => setShowSaveSet(false)}>Cancel</Button>
              </div>
            </div>
          )}
        </div>
      )}

      {totalCount === 0 && !suggestions.length && !labActions.length && (
        <EmptyBucket icon={TestTubes} message="No lab orders — generate a note first, then add labs above" />
      )}
    </div>
  );
}
